import * as React from "react";
import { useState, useEffect } from "react";
import axios from "axios";

import {
    Button,
    CircularProgress,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    InputAdornment,
    Chip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import WhatshotIcon from "@material-ui/icons/Whatshot";

import { Notification, useNotify } from "react-admin";
import { API } from "../Constant";
import { getConfigGet, getConfigPatch } from "../getConfig";

const useStyles = makeStyles((theme) => ({
    main: {
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        padding: theme.spacing(3),
        backgroundColor: "#f5f5f5",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: theme.spacing(3),
    },
    title: {
        fontWeight: 600,
    },
    subtitle: {
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
    },
    tableContainer: {
        borderRadius: 12,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    },
    tableHead: {
        backgroundColor: "#fafafa",
    },
    tableHeadCell: {
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    brandCell: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.5),
    },
    brandLogo: {
        width: 40,
        height: 40,
        objectFit: "contain",
        borderRadius: 4,
        backgroundColor: "#fff",
        border: "1px solid #e0e0e0",
    },
    brandName: {
        fontWeight: 500,
        marginLeft: 8,
    },
    overrideActive: {
        backgroundColor: "#e8f5e9",
        color: "#2e7d32",
        fontWeight: 500,
    },
    overrideInactive: {
        backgroundColor: "#fafafa",
        color: "#9e9e9e",
    },
    boostActive: {
        backgroundColor: "#fff3e0",
        color: "#e65100",
        fontWeight: 500,
    },
    editButton: {
        minWidth: 36,
        padding: theme.spacing(1),
    },
    loadingContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "50vh",
    },
    dialogField: {
        marginBottom: theme.spacing(2),
    },
    referenceSection: {
        backgroundColor: "#f5f5f5",
        borderRadius: 8,
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    referenceTitle: {
        fontWeight: 600,
        marginBottom: theme.spacing(1),
        color: theme.palette.text.secondary,
        fontSize: 12,
        textTransform: "uppercase",
    },
    referenceItem: {
        display: "flex",
        justifyContent: "space-between",
        padding: theme.spacing(0.5, 0),
        fontSize: 13,
    },
    searchField: {
        marginBottom: theme.spacing(2),
        minWidth: 300,
    },
}));

interface BrandMapping {
    id: number;
    brand_name: string;
    brand_name_override: string;
    brand_logo_override: string | null;
    sprive_haircut_override_percentage: number | null;
    sprive_haircut_override_from_date: string | null;
    sprive_haircut_override_to_date: string | null;
    boost_haircut_override_percentage: number | null;
    discount_override_percentage: number | null;
    original_logo_url: string | null;
    reference_gift_cards: Array<{
        id: number;
        discount: string;
        sprive_haircut_percentage: string;
        status: string;
    }>;
}

interface EditFormData {
    sprive_haircut_override_percentage: string;
    sprive_haircut_override_from_date: string;
    sprive_haircut_override_to_date: string;
    boost_haircut_override_percentage: string;
}

const BRAND_HAIRCUT_CONFIG_ENDPOINT = API + "/admin/brand-haircut-config";

const BrandHaircutConfig = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [brands, setBrands] = useState<BrandMapping[]>([]);
    const [filteredBrands, setFilteredBrands] = useState<BrandMapping[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedBrand, setSelectedBrand] = useState<BrandMapping | null>(null);
    const [editFormData, setEditFormData] = useState<EditFormData>({
        sprive_haircut_override_percentage: "",
        sprive_haircut_override_from_date: "",
        sprive_haircut_override_to_date: "",
        boost_haircut_override_percentage: "",
    });
    const notify = useNotify();
    const classes = useStyles();

    useEffect(() => {
        fetchBrands();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (searchTerm.trim() === "") {
            setFilteredBrands(brands);
        } else {
            const term = searchTerm.toLowerCase();
            setFilteredBrands(
                brands.filter(
                    (b) =>
                        b.brand_name.toLowerCase().includes(term) ||
                        b.brand_name_override?.toLowerCase().includes(term)
                )
            );
        }
    }, [searchTerm, brands]);

    const fetchBrands = async () => {
        try {
            setLoading(true);
            const config = getConfigGet({});
            const response: any = await axios.get(BRAND_HAIRCUT_CONFIG_ENDPOINT, config);
            if (response.data?.data) {
                setBrands(response.data.data);
                setFilteredBrands(response.data.data);
            }
        } catch (error) {
            console.error("Failed to fetch brands:", error);
            notify("Failed to load brand configurations", "warning");
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = (brand: BrandMapping) => {
        setSelectedBrand(brand);
        setEditFormData({
            sprive_haircut_override_percentage: brand.sprive_haircut_override_percentage?.toString() || "",
            sprive_haircut_override_from_date: brand.sprive_haircut_override_from_date?.split("T")[0] || "",
            sprive_haircut_override_to_date: brand.sprive_haircut_override_to_date?.split("T")[0] || "",
            boost_haircut_override_percentage: brand.boost_haircut_override_percentage?.toString() || "",
        });
        setEditDialogOpen(true);
    };

    const handleEditClose = () => {
        setEditDialogOpen(false);
        setSelectedBrand(null);
    };

    const handleSave = async () => {
        if (!selectedBrand) return;

        setSaving(true);
        try {
            const payload: any = {};

            // Only include fields that have values
            if (editFormData.sprive_haircut_override_percentage !== "") {
                payload.sprive_haircut_override_percentage = parseFloat(editFormData.sprive_haircut_override_percentage);
            } else {
                payload.sprive_haircut_override_percentage = null;
            }

            if (editFormData.sprive_haircut_override_from_date) {
                payload.sprive_haircut_override_from_date = editFormData.sprive_haircut_override_from_date;
            } else {
                payload.sprive_haircut_override_from_date = null;
            }

            if (editFormData.sprive_haircut_override_to_date) {
                payload.sprive_haircut_override_to_date = editFormData.sprive_haircut_override_to_date;
            } else {
                payload.sprive_haircut_override_to_date = null;
            }

            if (editFormData.boost_haircut_override_percentage !== "") {
                payload.boost_haircut_override_percentage = parseFloat(editFormData.boost_haircut_override_percentage);
            } else {
                payload.boost_haircut_override_percentage = null;
            }

            const headers = getConfigPatch();
            await axios.patch(
                `${BRAND_HAIRCUT_CONFIG_ENDPOINT}/${selectedBrand.id}`,
                payload,
                { headers }
            );

            notify(`Updated ${selectedBrand.brand_name} successfully`, "info");
            handleEditClose();
            fetchBrands();
        } catch (error: any) {
            notify(
                error?.response?.data?.message || "Failed to update brand configuration",
                "error"
            );
        } finally {
            setSaving(false);
        }
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return "-";
        return new Date(dateString).toLocaleDateString();
    };

    const formatPercentage = (value: number | null) => {
        if (value === null || value === undefined) return "-";
        return `${value}%`;
    };

    const isOverrideActive = (brand: BrandMapping) => {
        if (brand.sprive_haircut_override_percentage === null) return false;
        const now = new Date();
        const from = brand.sprive_haircut_override_from_date ? new Date(brand.sprive_haircut_override_from_date) : null;
        const to = brand.sprive_haircut_override_to_date ? new Date(brand.sprive_haircut_override_to_date) : null;
        if (from && now < from) return false;
        if (to && now > to) return false;
        return true;
    };

    if (loading) {
        return (
            <div className={classes.loadingContainer}>
                <CircularProgress size={40} />
            </div>
        );
    }

    return (
        <div className={classes.main}>
            <div className={classes.header}>
                <div>
                    <Typography variant="h5" className={classes.title}>
                        Brand Haircut Configuration
                    </Typography>
                    <Typography variant="body2" className={classes.subtitle}>
                        Manage Sprive haircut and boost haircut overrides for gift card brands
                    </Typography>
                </div>
                <TextField
                    className={classes.searchField}
                    variant="outlined"
                    size="small"
                    placeholder="Search brands..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            <TableContainer component={Paper} className={classes.tableContainer}>
                <Table>
                    <TableHead className={classes.tableHead}>
                        <TableRow>
                            <TableCell className={classes.tableHeadCell}>Brand</TableCell>
                            <TableCell className={classes.tableHeadCell}>Base Discount</TableCell>
                            <TableCell className={classes.tableHeadCell}>Base Haircut</TableCell>
                            <TableCell className={classes.tableHeadCell}>Haircut Override</TableCell>
                            <TableCell className={classes.tableHeadCell}>Override Period</TableCell>
                            <TableCell className={classes.tableHeadCell}>
                                <WhatshotIcon style={{ fontSize: 16, verticalAlign: "middle", marginRight: 4, color: "#ff6d00" }} />
                                Boost Haircut
                            </TableCell>
                            <TableCell className={classes.tableHeadCell} align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredBrands.map((brand) => {
                            const enabledCards = brand.reference_gift_cards?.filter(
                                (gc) => gc.status === "ENABLED"
                            ) || [];
                            const baseDiscount = enabledCards.length > 0 ? enabledCards[0].discount : "-";
                            const baseHaircut = enabledCards.length > 0 ? enabledCards[0].sprive_haircut_percentage : "-";

                            return (
                                <TableRow key={brand.id} hover>
                                    <TableCell>
                                        <div className={classes.brandCell}>
                                            {(brand.brand_logo_override || brand.original_logo_url) && (
                                                <img
                                                    src={brand.brand_logo_override || brand.original_logo_url || ""}
                                                    alt={brand.brand_name}
                                                    className={classes.brandLogo}
                                                />
                                            )}
                                            <div>
                                                <div className={classes.brandName}>
                                                    {brand.brand_name_override || brand.brand_name}
                                                </div>
                                                {brand.brand_name_override && brand.brand_name !== brand.brand_name_override && (
                                                    <div style={{ fontSize: 11, color: "#999" }}>
                                                        ({brand.brand_name})
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell>{baseDiscount}%</TableCell>
                                    <TableCell>{baseHaircut}%</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={formatPercentage(brand.sprive_haircut_override_percentage)}
                                            size="small"
                                            className={
                                                isOverrideActive(brand)
                                                    ? classes.overrideActive
                                                    : classes.overrideInactive
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {brand.sprive_haircut_override_from_date || brand.sprive_haircut_override_to_date ? (
                                            <span style={{ fontSize: 12 }}>
                                                {formatDate(brand.sprive_haircut_override_from_date)} - {formatDate(brand.sprive_haircut_override_to_date)}
                                            </span>
                                        ) : (
                                            "-"
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {brand.boost_haircut_override_percentage !== null ? (
                                            <Chip
                                                icon={<WhatshotIcon style={{ fontSize: 14 }} />}
                                                label={formatPercentage(brand.boost_haircut_override_percentage)}
                                                size="small"
                                                className={classes.boostActive}
                                            />
                                        ) : (
                                            <span style={{ color: "#9e9e9e" }}>-</span>
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Button
                                            size="small"
                                            className={classes.editButton}
                                            onClick={() => handleEditClick(brand)}
                                        >
                                            <EditIcon fontSize="small" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Edit Dialog */}
            <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Edit Haircut Configuration
                    {selectedBrand && (
                        <Typography variant="body2" color="textSecondary">
                            {selectedBrand.brand_name_override || selectedBrand.brand_name}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    {/* Reference Information */}
                    {selectedBrand && selectedBrand.reference_gift_cards?.length > 0 && (
                        <div className={classes.referenceSection}>
                            <div className={classes.referenceTitle}>Reference Data (Read-only)</div>
                            {selectedBrand.reference_gift_cards
                                .filter((gc) => gc.status === "ENABLED")
                                .map((gc, idx) => (
                                    <div key={idx} className={classes.referenceItem}>
                                        <span>Gift Card ID: {gc.id}</span>
                                        <span>Discount: {gc.discount}%</span>
                                        <span>Base Haircut: {gc.sprive_haircut_percentage}%</span>
                                    </div>
                                ))}
                        </div>
                    )}

                    <Typography variant="subtitle2" style={{ marginBottom: 12, fontWeight: 600 }}>
                        Sprive Haircut Override
                    </Typography>
                    <TextField
                        className={classes.dialogField}
                        label="Override Percentage"
                        variant="outlined"
                        fullWidth
                        size="small"
                        type="number"
                        value={editFormData.sprive_haircut_override_percentage}
                        onChange={(e) =>
                            setEditFormData({ ...editFormData, sprive_haircut_override_percentage: e.target.value })
                        }
                        InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        helperText="Leave empty to use base haircut"
                    />
                    <TextField
                        className={classes.dialogField}
                        label="From Date"
                        variant="outlined"
                        fullWidth
                        size="small"
                        type="date"
                        value={editFormData.sprive_haircut_override_from_date}
                        onChange={(e) =>
                            setEditFormData({ ...editFormData, sprive_haircut_override_from_date: e.target.value })
                        }
                        InputLabelProps={{ shrink: true }}
                        helperText="Optional - leave empty for no start date"
                    />
                    <TextField
                        className={classes.dialogField}
                        label="To Date"
                        variant="outlined"
                        fullWidth
                        size="small"
                        type="date"
                        value={editFormData.sprive_haircut_override_to_date}
                        onChange={(e) =>
                            setEditFormData({ ...editFormData, sprive_haircut_override_to_date: e.target.value })
                        }
                        InputLabelProps={{ shrink: true }}
                        helperText="Optional - leave empty for no end date"
                    />

                    <Typography variant="subtitle2" style={{ marginTop: 16, marginBottom: 12, fontWeight: 600 }}>
                        <WhatshotIcon style={{ fontSize: 18, verticalAlign: "middle", marginRight: 4, color: "#ff6d00" }} />
                        Boost Haircut Override
                    </Typography>
                    <TextField
                        className={classes.dialogField}
                        label="Boost Haircut Percentage"
                        variant="outlined"
                        fullWidth
                        size="small"
                        type="number"
                        value={editFormData.boost_haircut_override_percentage}
                        onChange={(e) =>
                            setEditFormData({ ...editFormData, boost_haircut_override_percentage: e.target.value })
                        }
                        InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        helperText="Haircut applied when user has active boost. Leave empty to use global boost haircut from referral config."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleEditClose} color="default">
                        Cancel
                    </Button>
                    <Button onClick={handleSave} color="primary" variant="contained" disabled={saving}>
                        {saving && <CircularProgress size={16} style={{ marginRight: 8 }} />}
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Notification />
        </div>
    );
};

export default BrandHaircutConfig;
